<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            ajax-path="cloudflare/accounts"
            @ajaxResponse="handleAjaxResponse"
        />
        <app-modal
            :show.sync="showZonesModal"
            width="lg:w-2/3"
        >
            <template slot="header">{{ selectedAccount.name }} Zones</template>
            <template slot="body">
                <tabulator
                    :columns="zonesColumns"
                    :actions="zonesActions"
                    :refresh.sync="refreshZonesTable"
                    :loading="zonesTableIsLoading"
                    :loading-text="zonesTableLoadingText"
                    :ajax-path="`cloudflare/accounts/${selectedAccount.id}/zones`"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showRecordsModal"
            width="lg:w-4/5"
        >
            <template slot="header">{{ selectedZone.name }} Records</template>
            <template slot="body">
                <tabulator
                    :columns="recordsColumns"
                    :actions="recordsActions"
                    :refresh.sync="refreshRecordsTable"
                    :ajax-path="`cloudflare/zones/${selectedZone.id}/records`"
                />
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateZoneModal"
            width="lg:w-1/3"
        >
            <template slot="header">Create Zone for {{ selectedAccount.name }}</template>
            <template slot="body">
                <app-input
                    v-model="createZoneForm.name"
                    placeholder="Domain Name"
                    :error="createZoneForm.errors.get('name')"
                    @updated="createZoneForm.errors.clear('name')"
                    required
                />
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateZoneModal = false"
                        :disable="creatingZone"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createZone"
                        :loading="creatingZone"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showCreateRecordModal"
            width="lg:w-1/3"
        >
            <template slot="header">Create Record for {{ selectedZone.name }}</template>
            <template slot="body">
                <app-input
                    v-model="createRecordForm.name"
                    placeholder="Name"
                    :error="createRecordForm.errors.get('name')"
                    @updated="createRecordForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="createRecordForm.type"
                    placeholder="Type"
                    :error="createRecordForm.errors.get('type')"
                    @updated="createRecordForm.errors.clear('type')"
                    :options="['A', 'AAAA', 'CNAME', 'TXT', 'SRV', 'LOC', 'MX', 'NS', 'SPF', 'CERT', 'DNSKEY', 'DS', 'NAPTR', 'SMIMEA', 'SSHFP', 'TLSA', 'URI']"
                    required
                />
                <app-input
                    v-model="createRecordForm.ttl"
                    placeholder="TTL (seconds)"
                    :error="createRecordForm.errors.get('ttl')"
                    @updated="createRecordForm.errors.clear('ttl')"
                    :integer="true"
                    required
                />
                <app-textarea
                    v-model="createRecordForm.content"
                    placeholder="Value"
                    :error="createRecordForm.errors.get('content')"
                    @updated="createRecordForm.errors.clear('content')"
                    required
                />
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showCreateRecordModal = false"
                        :disable="creatingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="createRecord"
                        :loading="creatingRecord"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditRecordModal"
            width="lg:w-1/3"
        >
            <template slot="header">Edit Record for {{ selectedZone.name }}</template>
            <template slot="body">
                <app-input
                    v-model="editRecordForm.name"
                    placeholder="Name"
                    :error="editRecordForm.errors.get('name')"
                    @updated="editRecordForm.errors.clear('name')"
                    required
                    focus
                />
                <app-select
                    v-model="editRecordForm.type"
                    placeholder="Type"
                    :error="editRecordForm.errors.get('type')"
                    @updated="editRecordForm.errors.clear('type')"
                    :options="['A', 'AAAA', 'CNAME', 'TXT', 'SRV', 'LOC', 'MX', 'NS', 'SPF', 'CERT', 'DNSKEY', 'DS', 'NAPTR', 'SMIMEA', 'SSHFP', 'TLSA', 'URI']"
                    required
                />
                <app-input
                    v-model="editRecordForm.ttl"
                    placeholder="TTL (seconds)"
                    :error="editRecordForm.errors.get('ttl')"
                    @updated="editRecordForm.errors.clear('ttl')"
                    :integer="true"
                    required
                />
                <app-textarea
                    v-model="editRecordForm.content"
                    placeholder="Value"
                    :error="editRecordForm.errors.get('content')"
                    @updated="editRecordForm.errors.clear('content')"
                    required
                />
                <div class="text-right mt-5">
                    <app-button
                        class="btn-white"
                        @click="showEditRecordModal = false"
                        :disable="editingRecord"
                    >Cancel</app-button>
                    <app-button
                        class="btn-blue ml-3"
                        @click="editRecord"
                        :loading="editingRecord"
                    >Save</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showNameserverSetupModal"
            width="lg:w-1/2"
        >
            <template v-if="selectedZone.status === 'active'">
                <template slot="header">Nameserver setup</template>
                <template slot="body">{{ selectedZone.name }} is already delegated to Cloudflare.</template>
            </template>
            <template v-else>
                <template slot="header">
                    <div>Complete your nameserver setup</div>
                    <div class="lowercase text-gray-600">{{ selectedZone.name }} <span v-if="selectedZone.status === 'active'">is now active</span><span v-else>is not yet active</span> on <span class="capitalize">Cloudflare</span>.</div>
                </template>
                <template slot="body">
                    <h2 class="font-bold">1. Log in to your registrant account.</h2>
                    <div class="text-sm text-gray-600">Determine your registrar via <a href="https://lookup.icann.org/" target="_blank">WHOIS</a></div><br>

                    <div>Remove these nameservers:</div>
                    <div class="mt-2 bg-gray-300 p-3 rounded block">
                       <div 
                            v-for="server in selectedZone.original_name_servers"
                            :key="server"
                        >{{ server }}</div> 
                    </div><br>
                    <h2 class="font-bold">2. Replace with Cloudflare's nameservers.</h2>
                    <div class="mt-3">Nameserver 1:</div>
                    <div class="mt-2 bg-gray-300 p-3 rounded block">
                        <div class="flex items-center justify-between">
                            <div>{{ selectedZone.name_servers[0] }}</div>
                            <app-button v-copy :data-clipboard-text="selectedZone.name_servers[0]" class="btn-blue">Copy</app-button>
                        </div>
                    </div>
                    <div class="mt-4">Nameserver 2:</div>
                    <div class="mt-2 bg-gray-300 p-3 rounded block">
                       <div class="flex items-center justify-between">
                            <div>{{ selectedZone.name_servers[1] }}</div>
                            <app-button v-copy :data-clipboard-text="selectedZone.name_servers[1]" class="btn-blue">Copy</app-button>
                        </div>
                    </div>
                    <div class="mt-4"><span class="font-bold">Save</span> your changes</div>
                    <div class="mt-3">Registrars typically process nameserver updates within 24 hours. Once this process completes, Cloudflare confirms your site activation via email.</div>
                </template>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Form from '@/helpers/Form';
    import Http from '@/helpers/Http';
    
    export default {
        data() {
            return {                
                refreshTable: false,
                tableIsLoading: false,
                tableLoadingText: null,
                showZonesModal: false,
                showRecordsModal: false,
                showCreateZoneModal: false,
                showCreateRecordModal: false,
                showEditRecordModal: false,
                showNameserverSetupModal: false,
                zonesTableIsLoading: false,
                zonesTableLoadingText: null,
                refreshZonesTable: false,
                refreshRecordsTable: false,
                creatingZone: false,
                creatingRecord: false,
                editingRecord: false,
                selectedAccount: {},
                selectedRecord: {},
                selectedZone: {
                    name_servers: []
                },
                createZoneForm: new Form({
                    name: null,
                }),
                createRecordForm: new Form({
                    name: null,
                    type: null,
                    content: null,
                    ttl: 1,
                }),
                editRecordForm: new Form({
                    name: null,
                    type: null,
                    content: null,
                    ttl: 1,
                }),
                recordsColumns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 350,
                        headerSortStartingDir: 'desc',
                    },
                    {
                        title: 'Type',
                        field: 'type',
                        sorter: 'string',
                        minWidth: 100,
                    },
                    {
                        title: 'Value',
                        field: 'content',
                        sorter: 'string',
                        minWidth: 600,
                    },
                    {
                        title: 'TTL',
                        field: 'ttl',
                        sorter: 'string',
                        minWidth: 70
                    },
                    {
                        title: 'Proxied',
                        field: 'proxied',
                        headerSort: false,
                        minWidth: 70,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return 'Yes';
                            }

                            return 'No'
                        }
                    },
                    {
                        title: 'Proxiable',
                        field: 'proxiable',
                        headerSort: false,
                        minWidth: 70,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return 'Yes';
                            }

                            return 'No'
                        }
                    }
                ],
                recordsActions: [
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateRecordModal = true;
                            this.createRecordForm.reset();
                            this.createRecordForm.errors.clear();
                        }
                    },
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: records => {
                            this.selectedRecord = records[0];
                            this.editRecordForm.set(records[0]);
                            this.showEditRecordModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        requiredSelect: true,
                        handler: records => {
                            this.deleteRecord(records[0]);
                        }
                    },
                ],
                zonesColumns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Paused',
                        field: 'paused',
                        headerSort: false,
                        minWidth: 100,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return 'Yes';
                            }

                            return 'No';
                        }
                    },
                    {
                        title: 'Status',
                        field: 'status',
                        headerSort: false,
                        minWidth: 100,
                    }
                ],
                zonesActions: [
                    {
                        label: 'Records',
                        icon: () => import('@/icons/AssignmentOutlined.vue'),
                        handler: zones => {
                            this.selectedZone = zones[0];
                            this.showRecordsModal = true;
                        }
                    },
                    {
                        label: 'Nameserver Setup',
                        icon: () => import('@/icons/DNSOutlined.vue'),
                        handler: zones => {
                            this.selectedZone = zones[0];
                            this.showNameserverSetupModal = true;
                        }
                    },
                    {
                        label: 'Delete',
                        icon: () => import('@/icons/DeleteOutlined.vue'),
                        handler: zones => {
                            this.deleteZone(zones[0]);
                        }
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.showCreateZoneModal = true;
                            this.createZoneForm.reset();
                            this.createZoneForm.errors.clear();
                        }
                    },
                ],
                columns: [
                    {
                        title: 'Name',
                        field: 'name',
                        sorter: 'string',
                        minWidth: 250,
                    },
                    {
                        title: 'Type', 
                        field: 'type', 
                        sorter: 'string',
                        minWidth: 200,
                    },
                ],
                actions: [
                    {
                        label: 'Zones',
                        icon: () => import('@/icons/LocationOnOutlined.vue'),
                        handler: accounts => {
                            this.getZones(accounts[0]);
                        }
                    },                 
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync,
                    },
                ],
            }
        },
        methods: {
            getZones(account) {
                this.selectedAccount = account;
                this.showZonesModal = true;
            },
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                Http.patch('cloudflare/accounts/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                        this.$toast.fire({
                            type: 'success',
                            title: 'Syncing complete.',
                        })
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    })
            },
            createZone() {
                this.creatingZone = true;
                this.createZoneForm.errors.clear();
                Http.post(`cloudflare/accounts/${this.selectedAccount.id}/zones`, this.createZoneForm.payload())
                    .then(({ data }) => {
                        this.creatingZone = false;
                        this.showCreateZoneModal = false;
                        this.refreshZonesTable = true;
                        this.selectedZone = data;
                        this.showNameserverSetupModal = true;
                    })
                    .catch(({ response }) => {
                        this.creatingZone = false;
                        this.createZoneForm.errors.record(response.data);
                    })
            },
            createRecord() {
                this.creatingRecord = true;
                this.createRecordForm.errors.clear();
                Http.post(`cloudflare/zones/${this.selectedZone.id}/records`, this.createRecordForm.payload())
                    .then(({ data }) => {
                        this.creatingRecord = false;
                        this.showCreateRecordModal = false;
                        this.refreshRecordsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Record was successfully created.'
                        });
                    })
                    .catch(({ response }) => {
                        this.creatingRecord = false;
                        this.createRecordForm.errors.record(response.data);
                    })
            },
            editRecord() {
                this.editingRecord = true;
                this.editRecordForm.errors.clear();
                Http.patch(`cloudflare/zones/${this.selectedZone.id}/records/${this.selectedRecord.id}`, this.editRecordForm.payload())
                    .then(({ data }) => {
                        this.editingRecord = false;
                        this.showEditRecordModal = false;
                        this.refreshRecordsTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success',
                            text: 'Record was successfully updated.'
                        });
                    })
                    .catch(({ response }) => {
                        this.editingRecord = false;
                        this.editRecordForm.errors.record(response.data);
                    })
            },
            deleteRecord(record) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${record.name}</strong>" record permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`cloudflare/zones/${this.selectedZone.id}/records/${record.id}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Record was successfully deleted.'
                        })
                    }
                });
            },
            deleteZone(zone) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to delete "<strong>${zone.name}</strong>" zone permanently.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, delete it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.delete(`cloudflare/accounts/${this.selectedAccount.id}/zones/${zone.id}`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.refreshTable = true;
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Zone was successfully deleted.'
                        })
                    }
                });
            },
            handleAjaxResponse(response) {
            },      
        }
    }
</script>